/**
 * Answers `?apply=true` exactly once per offer per tab.
 *
 * The guard used to be a `useRef` inside the modal, and the event it drives proved that is not
 * enough: measured on production, 95 `apply_intent_resumed` from 26 people on 21/09/2026,
 * about four each. A ref lives as long as the mount, the component remounts while the
 * parameter is still in the address, and every remount answered the same intent again. That
 * both inflates the event four-fold and re-opens the apply confirmation, which invites a
 * second application to an offer the person has already applied to.
 *
 * Module scope, so it outlives a remount and lasts as long as the tab, which is the life of
 * the intent. Keyed by the offer path rather than by a boolean: a visitor who resumes on one
 * offer and then follows the same route into a different one in the same tab has two intents,
 * and both are theirs.
 */
const consumedApplyIntents = new Set<string>();

/**
 * The path without its query, so `?scout=` or a stripped `apply` cannot split one intent in
 * two, and without the locale prefix or a trailing slash, so `/it/posizione/x`,
 * `/en/posizione/x` and `/posizione/x/` are the one offer they actually are.
 */
export const applyIntentKey = (asPath: string): string => {
  const path = asPath.split('?')[0].replace(/^\/[a-z]{2}(?=\/)/, '');
  return path.length > 1 ? path.replace(/\/$/, '') : path;
};

/**
 * True the first time this offer's intent is claimed, false afterwards. The caller acts only
 * on a true: claiming and acting are one step on purpose, because two steps leave room for a
 * second render to slip between them.
 */
export const claimApplyIntent = (asPath: string): boolean => {
  const key = applyIntentKey(asPath);
  if (consumedApplyIntents.has(key)) return false;
  consumedApplyIntents.add(key);
  return true;
};

/** Test seam: module state would otherwise leak between cases. */
export const resetApplyIntentGuard = () => consumedApplyIntents.clear();

/** Where a resumed `?apply=true` lands, which is not the same screen on every page. */
export interface ApplyIntentTarget {
  /**
   * Whether to open `JobApplyConfirmation`, the "do you confirm you want to apply for this job
   * position?" screen. It applies with `useJobPositionId()`, so it only makes sense where that
   * id belongs to the page being looked at.
   */
  openConfirmation: boolean;
  surface: 'job_position' | 'venue';
}

/** Next's `router.pathname` for the venue showcase, locale prefix already stripped by Next. */
const VENUE_ROUTE = /^\/locale\//;

/**
 * A venue page (`/locale/<slug>`) has no job position: applying there is spontaneous, towards
 * the restaurant. Opening the offer confirmation on it produced a modal with no offer card and
 * a primary button that posted an empty `job_position_id` with no `restaurant_id`, which cannot
 * succeed and did not even report its own failure. Measured on production 18-22/09: about ten
 * people a day, all of whom had already signed in and come back.
 *
 * The surface is decided by the ROUTE and never by the job-position id, and that distinction is
 * the whole point. `jobPositionApiDataAtom` is filled on the offer page and **never cleared**,
 * and the Jotai `Provider` lives in `_app`, so it survives every client-side navigation. Reading
 * the id to decide where we are would therefore say "job position" on a venue reached from an
 * offer in the same tab, and the confirmation would apply for the PREVIOUS offer: a real
 * application to something the visitor never chose. Raised by the Codex peer review, confirmed
 * in `_app.tsx` and in the single writer of that atom.
 */
export const resumeTargetFor = ({
  pathname,
  jobPositionId,
}: {
  /** `router.pathname`, the route pattern, not the filled-in address. */
  pathname: string;
  jobPositionId?: string;
}): ApplyIntentTarget =>
  VENUE_ROUTE.test(pathname)
    ? { openConfirmation: false, surface: 'venue' }
    : { openConfirmation: !!jobPositionId, surface: 'job_position' };
